import { Logout, ManageAccounts } from '@mui/icons-material'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
//* NOTE: MomentJS Locales
import 'moment/dist/locale/es'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/pt'
import 'moment/dist/locale/pt-br'
import 'moment/dist/locale/sk'
import { NavLink, useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'
import { useWindowSize } from 'usehooks-ts'

import { axios } from '~/axios'
import PlatformLanguageSelector from '~/components/LanguageSelector'
import DesktopMenu from '~/components/header/DesktopMenu'
import HamburgerMenu from '~/components/header/HamburgerMenu'
import NotificationsMenu from '~/components/header/NotificationsMenu'
import ImpersonateUserModal from '~/features/Auth/components/ImpersonateUserModal'
import { canImpersonateUser } from '~/features/Auth/utils/can-impersonate-user'
import { updateUser } from '~/redux/slices/user'
import { logout } from '~/services/auth-services'
import { addSupersetTab } from '~/utils/header-menu-utils'
import { useRole } from '~/utils/hooks/useRole'
import { permissionsItems } from '~/utils/permissions-utils'
import {
  ROLES,
  ROLES_MAP,
  canAccess,
  checkAccessToInterventions,
  checkAccessToProjects,
  checkAccessToSuperset,
} from '~/utils/role-utils'

import { useAppDispatch, useAppSelector } from './redux/hooks'
import { op } from './utils/open-panel-utils'

export interface MenuTab {
  item: string
  options?: string[]
  shouldShowOnHeader: boolean
}

export default function Header() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const intercom = useIntercom()
  const windowSize = useWindowSize()
  const user = useAppSelector(state => state.user)

  const { userHasAnyRole } = useRole()
  const queryClient = useQueryClient()

  const [userAnchorEl, setUserAnchorEl] = useState<HTMLElement | null>(null)
  const [isImpersonateOpened, setIsImpersonateOpened] = useState(false)
  const canImpersonate = canImpersonateUser(user, window.location.hostname)

  const firstCharOfName = user?.name?.at(0) ?? user?.username?.at(0)

  const roleTabs = useMemo(() => {
    if (!user) return []
    const { permissions } = user

    const tabsByRole = ROLES_MAP[user.role] ?? []
    const roleTabs = tabsByRole
      .filter(({ item: page, shouldShowOnHeader }) => {
        if (page === 'projects') return checkAccessToProjects(user)
        if (page === 'solutions') return canAccess(user, 'solutions')
        if (page === 'interventions') return checkAccessToInterventions(user)

        return shouldShowOnHeader
      })
      .filter(({ item: page }) => {
        const hasUserPermissions = !!permissions && permissions.length > 1
        if (!hasUserPermissions) return true

        const hasUserAccessToPage = permissionsItems.some(p => p.item === page)
        return !hasUserAccessToPage
      })
      .filter(({ item: page }) => {
        const canSeeProjects = userHasAnyRole([ROLES.ORGANIZATION_ADMIN])

        if (!canSeeProjects && page === 'projects') return false
        return true
      })
      .map(tab => {
        if (tab.options?.includes('superset') && !checkAccessToSuperset(user)) {
          return { ...tab, options: tab.options.filter(o => o !== 'superset') }
        }
        return tab
      })

    return roleTabs
  }, [user, userHasAnyRole])

  const permissionTabs = useMemo(() => {
    if (!user) return []
    const permissions = user.permissions ?? []

    const permissionTabs: MenuTab[] = []
    const permissionsWithReadAccess = permissions
      .filter(p => p.split('_')[1] === 'read')
      .map(p => p.split('_')[0])

    permissionsItems.forEach(permissionItem => {
      permissionsWithReadAccess.forEach(permissionRead => {
        const { options, item } = permissionItem

        const hasTabOptions = options && options.length > 0

        const isModuleInItem = item === permissionRead
        const isModuleInOptions =
          options?.some(o => o === permissionRead) ?? false

        if (!isModuleInItem && !isModuleInOptions) return

        const permissionTabIndex = permissionTabs.findIndex(tab => {
          return tab.item === item
        })

        if (!hasTabOptions) {
          permissionTabs.push({
            item: permissionRead,
            shouldShowOnHeader: true,
          })
          return
        }

        if (permissionTabIndex !== -1) {
          const options = permissionTabs[permissionTabIndex].options ?? []

          permissionTabs[permissionTabIndex] = {
            shouldShowOnHeader: true,
            item: permissionItem.item,
            options: [...options, permissionRead],
          }
        } else {
          permissionTabs.push({
            shouldShowOnHeader: true,
            item: permissionItem.item,
            options: [permissionRead],
          })
        }
      })
    })

    return permissionTabs
  }, [user])

  const tabs = useMemo(() => {
    return addSupersetTab([...permissionTabs, ...roleTabs], user)
  }, [permissionTabs, roleTabs, user])

  async function handleLogout() {
    try {
      if (!user) return
      await logout()
    } finally {
      navigate('/login')
      dispatch(updateUser(null))
      void queryClient.resetQueries()

      intercom.update({
        name: '',
        email: '',
        userId: '',
      })

      op.clear()
      Sentry.setUser(null)

      // NOTE: Reset axios headers (in case of using the dynamic link)
      axios.defaults.headers.post['Stoplight-Client-Id'] = 'stoplight-web'
    }
  }

  //* NOTE: Min width calc is based on 105px width per tab plus three fixed options
  const canShowDesktopMenu = windowSize.width > (tabs.length + 3) * 105

  if (!user) return null

  return (
    <AppBar
      elevation={1}
      position="fixed"
      sx={{ bgcolor: theme => theme.palette.background.default }}
    >
      <Toolbar disableGutters sx={{ px: 1 }}>
        {canShowDesktopMenu ? (
          <DesktopMenu tabs={tabs} />
        ) : (
          <HamburgerMenu tabs={tabs} />
        )}

        {/* Extra Buttons */}
        <Stack direction="row" ml="auto" alignItems="center" gap={2}>
          {canShowDesktopMenu && (
            <NavLink
              key="support"
              to={'/support'}
              style={{ textDecoration: 'none' }}
            >
              <Typography variant="subtitle1">
                {t('views.toolbar.support')}
              </Typography>
            </NavLink>
          )}

          <PlatformLanguageSelector />

          {userHasAnyRole([ROLES.PS_TEAM, ROLES.HUB_ADMIN]) && (
            <NotificationsMenu />
          )}

          <Box>
            <IconButton
              onClick={event => {
                setUserAnchorEl(event.currentTarget)
              }}
              size="small"
              aria-controls={userAnchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userAnchorEl ? 'true' : undefined}
            >
              <Avatar
                sx={{ bgcolor: 'primary.main', textTransform: 'uppercase' }}
              >
                {firstCharOfName}
              </Avatar>
            </IconButton>
          </Box>
        </Stack>

        <Menu
          anchorEl={userAnchorEl}
          id="account-menu"
          open={Boolean(userAnchorEl)}
          onClose={() => {
            setUserAnchorEl(null)
          }}
          onClick={() => {
            setUserAnchorEl(null)
          }}
          slotProps={{
            paper: {
              sx: { bgcolor: theme => theme.palette.background.default },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              navigate('/profile')
            }}
          >
            <Avatar
              sx={{ bgcolor: 'primary.main', textTransform: 'uppercase' }}
            >
              {firstCharOfName}
            </Avatar>
            <Box sx={{ ml: 1 }}>
              <Typography fontWeight="bold" color="primary">
                {user.name}
              </Typography>
              <Typography fontSize="small">{user.username}</Typography>
            </Box>
          </MenuItem>
          {canImpersonate && (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  setUserAnchorEl(null)
                  setIsImpersonateOpened(true)
                }}
              >
                <ListItemIcon>
                  <ManageAccounts fontSize="small" />
                </ListItemIcon>
                {t('impersonate.button')}
              </MenuItem>
            </>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              void handleLogout()
            }}
          >
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            {t('views.toolbar.logout')}
          </MenuItem>
        </Menu>
        <ImpersonateUserModal
          isOpened={isImpersonateOpened}
          setIsOpened={setIsImpersonateOpened}
        />
      </Toolbar>
    </AppBar>
  )
}
